/**
 * The Docs tab (`/{ws}/docs`): every page and database I can open, as one flat list — filtered (All, Recent,
 * Created by me, Favorites, Databases), searched by title, newest edit first. Pure; the view model reads the page
 * tree's cache (logic/docs) so it stays live with the sidebar.
 */
import type { PageNode } from "@bundle/contracts";
import type { DocsCache } from "./docs.ts";

export type DocsFilter = "all" | "recent" | "mine" | "favorites" | "databases";

export const DOCS_FILTERS: readonly DocsFilter[] = ["all", "recent", "mine", "favorites", "databases"];

/** Recent = edited in the last 14 days. */
export const RECENT_WINDOW_MS = 14 * 86_400_000;

/** The pages the filter and search show, most recently edited first (favorites: in the order I added them). */
export function docsList(cache: DocsCache, filter: DocsFilter, query: string, meId: string, now: number): PageNode[] {
  const q = query.trim().toLowerCase();
  const byTitle = (p: PageNode) => q === "" || p.title.toLowerCase().includes(q);
  if (filter === "favorites") {
    return cache.favoriteIds.map((id) => cache.pages[id]).filter((p): p is PageNode => p !== undefined && byTitle(p));
  }
  return Object.values(cache.pages)
    .filter((p) => {
      if (!byTitle(p)) return false;
      if (filter === "recent") return now - p.updatedAt <= RECENT_WINDOW_MS;
      if (filter === "mine") return p.createdBy === meId;
      if (filter === "databases") return p.kind === "database";
      return true;
    })
    .toSorted((a, b) => b.updatedAt - a.updatedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The path above a page ("Projects / Q4"), for the row's subtitle; "" at the top level. */
export function docsPath(cache: DocsCache, page: PageNode): string {
  const parts: string[] = [];
  let cur = page.parentId === undefined ? undefined : cache.pages[page.parentId];
  for (let i = 0; cur !== undefined && i < 10; i++) {
    parts.unshift(cur.title === "" ? "Untitled" : cur.title);
    cur = cur.parentId === undefined ? undefined : cache.pages[cur.parentId];
  }
  return parts.join(" / ");
}
